import React, { useEffect, useRef, useState } from 'react';
import { updateSessionScorecard } from '../../api/sessionExtras';

/**
 * ScorecardTab - fixed competency rubric with 1–5 sliders + comment.
 *
 * Phase 1 keeps the rubric hard-coded. Phase 6+ may load org-defined rubrics.
 */
const RUBRIC = [
  { key: 'technical',     label: 'Technical depth' },
  { key: 'problemSolving',label: 'Problem solving' },
  { key: 'communication', label: 'Communication' },
  { key: 'collaboration', label: 'Collaboration / culture fit' },
];

export default function ScorecardTab({ sessionId, initial = {} }) {
  const [scores,     setScores]     = useState(() => normalize(initial));
  const [savedAt,    setSavedAt]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const lastSaved    = useRef(JSON.stringify(normalize(initial)));

  // Autosave on change (debounced 800ms)
  useEffect(() => {
    if (!sessionId) return;
    const payload = JSON.stringify(scores);
    if (payload === lastSaved.current) return;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await updateSessionScorecard(sessionId, scores);
        lastSaved.current = payload;
        setSavedAt(new Date());
      } catch (e) {
        console.warn('[ScorecardTab] save failed', e?.message);
      } finally {
        setSaving(false);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [scores, sessionId]);

  const setRating = (key, rating) =>
    setScores((s) => ({ ...s, [key]: { ...(s[key] || {}), rating } }));
  const setComment = (key, comment) =>
    setScores((s) => ({ ...s, [key]: { ...(s[key] || {}), comment } }));

  const recommendation = scores.recommendation || '';

  return (
    <div className="h-full overflow-y-auto p-3 space-y-3">
      {RUBRIC.map((r) => {
        const entry = scores[r.key] || { rating: 0, comment: '' };
        return (
          <div key={r.key} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-slate-200">{r.label}</span>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setRating(r.key, entry.rating === n ? 0 : n)}
                    className={`w-7 h-7 text-xs rounded font-bold border transition-colors ${
                      entry.rating >= n
                        ? 'bg-teal-600 border-teal-500 text-white'
                        : 'bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300'}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              rows={2}
              value={entry.comment || ''}
              onChange={(e) => setComment(r.key, e.target.value)}
              placeholder="Notes (optional)"
              className="mt-2 w-full resize-none rounded bg-slate-950 border border-slate-800
                         text-slate-200 text-xs p-2 focus:outline-none focus:ring-1
                         focus:ring-blue-500"
            />
          </div>
        );
      })}

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
        <p className="text-xs font-semibold text-slate-200 mb-2">Final recommendation</p>
        <div className="grid grid-cols-2 gap-2">
          {['STRONG_HIRE','HIRE','NO_HIRE','STRONG_NO_HIRE'].map((opt) => (
            <button
              key={opt}
              onClick={() => setScores((s) => ({ ...s, recommendation: opt }))}
              className={`py-1.5 text-[11px] rounded font-semibold border ${
                recommendation === opt
                  ? 'bg-teal-600 border-teal-500 text-white'
                  : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'}`}
            >
              {opt.replaceAll('_',' ')}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[10px] text-slate-500 text-right">
        {saving ? 'Saving…' : savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'Autosaves on change'}
      </p>
    </div>
  );
}

function normalize(initial) {
  const n = { ...(initial || {}) };
  for (const r of RUBRIC) {
    if (!n[r.key] || typeof n[r.key] !== 'object') n[r.key] = { rating: 0, comment: '' };
  }
  return n;
}
